import { useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { buildProductHandlesQuery, PRODUCT_NODE } from '@/components/product/data/productQuery';
import { getCornerImage, getMetafieldImage, getMetafieldV2 } from '@/utils/utils';
import { ProductWithColorImage } from '@/types/ecommerce.types';

export const useGalleryCornerImages = (tileHandles: string[]) => {
  const productsQuery = useMemo(() => buildProductHandlesQuery(tileHandles), [tileHandles]);

  const { data: tileData, loading: tilesLoading } = useQuery(productsQuery, {
    skip: !tileHandles.length,
  });

  const tiles = useMemo(
    () => (tileData ? Object.values(tileData).filter((p) => (p as any)?.id) : []),
    [tileData]
  );

  const collectionHandles = useMemo(() => {
    if (!tiles.length) return [] as string[];
    const handles = tiles
      .map((p: any) => getMetafieldV2('product_color_collection', p.metafields || []))
      .filter(Boolean);
    return Array.from(new Set(handles));
  }, [tiles]);

  const collectionsQuery = useMemo(() => {
    if (!collectionHandles.length) {
      return gql`
        query EmptyCollectionsQuery {
          __typename
        }
      `;
    }

    const body = collectionHandles
      .map(
        (handle, i) =>
          `c${i}: collectionByHandle(handle: "${handle}") { id title products(first: 10) { edges { node { ...ProductNode } } } }`
      )
      .join('\n');

    return gql`
      query GetCollectionsByHandles {
        ${body}
      }
      ${PRODUCT_NODE}
    `;
  }, [collectionHandles]);

  const { data: collectionsData, loading: collectionsLoading } = useQuery(collectionsQuery, {
    skip: !collectionHandles.length,
  });

  const collections = useMemo(
    () => (collectionsData ? Object.values(collectionsData).filter((c) => (c as any)?.id) : []),
    [collectionsData]
  );

  const images = useMemo(() => {
    if (tilesLoading || collectionsLoading || !tiles.length || !collections.length) return null;

    const tileTypes = new Set(tiles.map((p: any) => p.productType).filter(Boolean));
    const map: Record<string, { colorCode: string; image: string }> = {};

    collections.forEach((collection: any) => {
      collection.products?.edges?.forEach(({ node }: any) => {
        const colorCode = getMetafieldV2('color', node.metafields || []);
        if (!colorCode || !tileTypes.has(node.productType)) return;

        const colorName = getMetafieldV2('color_name', node.metafields || []);
        if (!colorName || !tileTypes.has(node.productType)) return;

        const colorCornerImage = getMetafieldImage(
          (node as ProductWithColorImage).color_image,
          node.images.edges
        );

        const url = colorCornerImage || getCornerImage(node.images.edges);

        if (url) {
          map[colorName.toLowerCase()] = {
            colorCode: colorCode,
            image: url,
          };
        }
      });
    });

    return Object.entries(map).map(([colorName, data]) => ({
      colorName,
      colorCode: data.colorCode,
      image: data.image,
    }));
  }, [tilesLoading, collectionsLoading, tiles, collections]);

  return images;
};
